/**
 * MLApiClient - Unified API Client for All ML Infrastructure Pages
 * 
 * Provides consistent API calls across all 9 ML pages with authentication,
 * error handling, and retry logic.
 * 
 * Following BRAIN-PRE-CODE-CHECKLIST.md:
 * - Clean single imports
 * - Always include /api prefix explicitly
 * - Use platform admin token from localStorage
 * - Defensive error handling
 * - No silent failures
 */

import { getBaseUrl } from '../../../../../../shared/utils/apiConfig';

const TOKEN_KEY = 'astrapeak_platform_admin_token';
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
const TIMEOUT_MS = 30000;

class MLApiClient {
  private baseUrl: string;
  private retryCount: number = MAX_RETRIES;
  private retryDelay: number = RETRY_DELAY_MS;

  constructor() {
    this.baseUrl = getBaseUrl();
  }

  private getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  private async fetchWithRetry<T>(
    endpoint: string,
    options: RequestInit = {},
    retries: number = this.retryCount
  ): Promise<T> {
    const token = this.getToken();
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
      const response = await fetch(`${this.baseUrl}/api${endpoint}`, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status >= 500 && retries > 0) {
          await new Promise(resolve => setTimeout(resolve, this.retryDelay));
          return this.fetchWithRetry<T>(endpoint, options, retries - 1);
        }
        
        const errorText = await response.text();
        throw new Error(`API Error (${response.status}): ${errorText || response.statusText}`);
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout - please try again');
        }
        // ⚠️ CIRCUIT BREAKER: Don't retry on network errors (prevents infinite loops)
        if (error.message.includes('Failed to fetch') && retries > 0) {
          console.warn(`Network error on ${endpoint}, retries remaining: ${retries - 1}`);
          // Only retry once for network errors, not 3 times
          if (retries === MAX_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, this.retryDelay));
            return this.fetchWithRetry<T>(endpoint, options, retries - 1);
          }
        }
      }
      
      throw error;
    }
  }

  // Model Registry endpoints
  async listModels(filters?: { tenant_id?: string; deployment_status?: string; include_deprecated?: boolean }) {
    const params = new URLSearchParams();
    if (filters?.tenant_id) params.append('tenant_id', filters.tenant_id);
    if (filters?.deployment_status) params.append('deployment_status', filters.deployment_status);
    if (filters?.include_deprecated !== undefined) params.append('include_deprecated', String(filters.include_deprecated));
    
    const query = params.toString();
    return this.fetchWithRetry(`/ml/models${query ? `?${query}` : ''}`);
  }

  async getModelMetadata(modelId: string, tenantId: string) {
    return this.fetchWithRetry(`/ml/models/${modelId}/metadata?tenant_id=${tenantId}`);
  }

  async updateDeploymentStatus(modelId: string, tenantId: string, status: string) {
    return this.fetchWithRetry(`/ml/models/${modelId}/deployment-status`, {
      method: 'PUT',
      body: JSON.stringify({ tenant_id: tenantId, deployment_status: status })
    });
  }

  async deleteModel(modelId: string, tenantId: string, force: boolean = false) {
    return this.fetchWithRetry(`/ml/models/${modelId}`, {
      method: 'DELETE',
      body: JSON.stringify({ tenant_id: tenantId, force })
    });
  }

  // Training Pipeline endpoints
  async getPipelineHealth() {
    return this.fetchWithRetry('/ml/pipeline/health');
  }

  async trainAllModels(tenantId: string, datasetTypes: string[] = ['synthetic'], sampleSize: number = 10000) {
    return this.fetchWithRetry('/platform-admin/ml/training/train-all', {
      method: 'POST',
      body: JSON.stringify({ 
        tenant_id: tenantId,
        dataset_types: datasetTypes,
        sample_size: sampleSize
      })
    });
  }

  async getTrainingStatus(tenantId: string) {
    return this.fetchWithRetry(`/platform-admin/ml/training/status/${tenantId}`);
  }

  async getTrainingHistory(
    tenantId: string,
    limit: number = 50,
    offset: number = 0,
    status?: string
  ) {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString()
    });
    
    if (status) {
      params.append('status', status);
    }
    
    return this.fetchWithRetry(`/ml/training/history/${tenantId}?${params.toString()}`);
  }

  async getTrainingLogs(runId: string, limit: number = 1000, level?: string) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (level) {
      params.append('level', level);
    }
    
    return this.fetchWithRetry(`/platform-admin/training/logs/${runId}?${params.toString()}`);
  }

  async getTrainingConfig(tenantId: string) {
    return this.fetchWithRetry(`/ml/training/config/${tenantId}`);
  }

  async updateTrainingConfig(tenantId: string, config: any) {
    return this.fetchWithRetry(`/ml/training/config/${tenantId}`, {
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  // M-005: Calibration metrics endpoint
  async getCalibrationMetrics(tenantId?: string, timeRangeHours: number = 24) {
    const params = new URLSearchParams();
    if (tenantId) params.append('tenant_id', tenantId);
    params.append('time_range_hours', timeRangeHours.toString());
    
    return this.fetchWithRetry(`/platform-admin/ml/calibration/metrics?${params.toString()}`);
  }

  // Inference Monitoring endpoints
  async getInferenceMetrics() {
    return this.fetchWithRetry('/ml/inference/metrics');
  }

  async getModelHealth() {
    return this.fetchWithRetry('/ml/health/models');
  }

  // Feedback Analytics endpoints
  async getFeedbackSources() {
    return this.fetchWithRetry('/ml/feedback/sources');
  }

  async getFeedbackDistribution() {
    return this.fetchWithRetry('/ml/feedback/distribution');
  }

  // Hyperparameter Tuning endpoints
  async startOptimization(params: any) {
    return this.fetchWithRetry('/ml/hyperparameter-optimization/start', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  async stopOptimization() {
    return this.fetchWithRetry('/ml/hyperparameter-optimization/stop', {
      method: 'POST'
    });
  }

  async getOptimizationStatus() {
    return this.fetchWithRetry('/ml/hyperparameter-optimization/status');
  }

  // Retraining endpoints - Use NEW Platform Admin endpoints
  async getRetrainingTriggers(tenantId: string) {
    return this.fetchWithRetry(`/platform-admin/ml/retraining/recommendation/${tenantId}`);
  }

  async triggerRetraining(tenantId: string) {
    return this.fetchWithRetry(`/platform-admin/ml/retraining/trigger`, {
      method: 'POST',
      body: JSON.stringify({ tenant_id: tenantId, force: false })
    });
  }

  async getRetrainingConfig(tenantId: string) {
    return this.fetchWithRetry(`/platform-admin/ml/retraining/config/${tenantId}`);
  }

  async updateRetrainingConfig(tenantId: string, config: any) {
    return this.fetchWithRetry(`/platform-admin/ml/retraining/config`, {
      method: 'PUT',
      body: JSON.stringify({ tenant_id: tenantId, ...config })
    });
  }

  async getRetrainingHistory(tenantId: string) {
    return this.fetchWithRetry(`/ml/retraining/history/${tenantId}`);
  }

  // MLflow endpoints - Use NEW Platform Admin endpoints
  async listExperiments(tenantId: string) {
    return this.fetchWithRetry(`/platform-admin/ml/mlflow/experiments/${tenantId}`);
  }

  async getExperimentRuns(tenantId: string, algorithm: string) {
    return this.fetchWithRetry(`/platform-admin/ml/mlflow/runs/${tenantId}/${algorithm}`);
  }

  async getRunArtifacts(runId: string) {
    return this.fetchWithRetry(`/platform-admin/ml/mlflow/run/${runId}`);
  }

  // Performance Analytics endpoints
  async getPerformanceInsights() {
    return this.fetchWithRetry('/ml/performance/insights');
  }

  // Tenant Management endpoints
  async listTenantsWithStatus() {
    return this.fetchWithRetry('/ml/training/tenants');
  }

  async trainSelectedTenants(tenantIds?: string[]) {
    return this.fetchWithRetry('/ml/training/models/train-all', {
      method: 'POST',
      body: JSON.stringify({ tenant_ids: tenantIds })
    });
  }
}

// Singleton instance
export const mlApiClient = new MLApiClient();
export default mlApiClient;
